// src/pages/sessions/components/LiveSession/hooks/useConnectionQuality.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { RoomEvent, ConnectionQuality } from 'livekit-client';

/**
 * Hook to track connection quality for all participants (local + remote)
 * 
 * Shows warnings for:
 * - ConnectionQuality.Poor (yellow) - after 5 consecutive seconds of poor quality
 * - ConnectionQuality.Lost (red) - immediately for truly severe connection issues
 * 
 * Returns:
 * - localQuality: ConnectionQuality for the local participant
 * - remoteQualities: Map of participantIdentity -> ConnectionQuality
 * - isLocalConnectionLost: boolean (true only for Lost)
 * - isLocalConnectionUnstable: boolean (true for Poor after 5s debounce)
 * - isEstablishing: boolean (true while ICE is still negotiating/stabilizing)
 * - getParticipantQuality: function to get quality for a specific participant
 * - unstableParticipants: Set of participant identities with sustained poor connection
 */

// Number of consecutive good/excellent readings required to consider connection stable
const STABLE_QUALITY_THRESHOLD = 3;
// Maximum time to wait for stable connection before showing actual quality (ms)
const MAX_ESTABLISHING_TIME = 15000;
// Time in ms that poor quality must persist before showing warning (5 seconds)
const POOR_QUALITY_DEBOUNCE_MS = 5000;

export function useConnectionQuality({ roomRef, roomConnected }) {
  const [localQuality, setLocalQuality] = useState(ConnectionQuality.Unknown);
  const [remoteQualities, setRemoteQualities] = useState(new Map());
  const [isEstablishing, setIsEstablishing] = useState(true);
  const [isLocalConnectionUnstable, setIsLocalConnectionUnstable] = useState(false);
  const [unstableParticipants, setUnstableParticipants] = useState(new Set());
  
  const mountedRef = useRef(true);
  const consecutiveGoodCountRef = useRef(0);
  const establishingTimeoutRef = useRef(null);
  
  // Debounce timers for poor quality detection
  const localPoorTimerRef = useRef(null);
  const remotePoorTimersRef = useRef(new Map()); // identity -> timerId
  
  // Lost quality triggers immediately
  const isLocalConnectionLost = localQuality === ConnectionQuality.Lost;
  
  const getParticipantQuality = useCallback((identity) => {
    return remoteQualities.get(identity) || ConnectionQuality.Unknown;
  }, [remoteQualities]);
  
  const isParticipantConnectionLost = useCallback((identity) => {
    const quality = remoteQualities.get(identity);
    return quality === ConnectionQuality.Lost;
  }, [remoteQualities]);
  
  const isParticipantConnectionUnstable = useCallback((identity) => {
    return unstableParticipants.has(identity);
  }, [unstableParticipants]);

  // Clear local poor timer
  const clearLocalPoorTimer = useCallback(() => {
    if (localPoorTimerRef.current) {
      clearTimeout(localPoorTimerRef.current);
      localPoorTimerRef.current = null;
    }
  }, []);

  // Clear remote poor timer for a specific participant
  const clearRemotePoorTimer = useCallback((identity) => {
    const timerId = remotePoorTimersRef.current.get(identity);
    if (timerId) {
      clearTimeout(timerId);
      remotePoorTimersRef.current.delete(identity);
    }
  }, []);

  // Reset establishing state when room disconnects
  useEffect(() => {
    if (!roomConnected) {
      setIsEstablishing(true);
      setIsLocalConnectionUnstable(false);
      setUnstableParticipants(new Set());
      consecutiveGoodCountRef.current = 0;
      clearLocalPoorTimer();
      
      // Clear all remote timers
      remotePoorTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      remotePoorTimersRef.current.clear();
      
      if (establishingTimeoutRef.current) {
        clearTimeout(establishingTimeoutRef.current);
        establishingTimeoutRef.current = null;
      }
    }
  }, [roomConnected, clearLocalPoorTimer]);

  // Fallback timeout - don't show "Connecting..." forever
  useEffect(() => {
    if (roomConnected && isEstablishing) {
      establishingTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setIsEstablishing(false);
        }
      }, MAX_ESTABLISHING_TIME);
      
      return () => {
        if (establishingTimeoutRef.current) {
          clearTimeout(establishingTimeoutRef.current);
        }
      };
    }
  }, [roomConnected, isEstablishing]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!roomRef.current || !roomConnected) {
      return;
    }
    
    const room = roomRef.current;
    
    // Initialize with current state
    if (room.localParticipant) {
      const initialQuality = room.localParticipant.connectionQuality || ConnectionQuality.Unknown;
      setLocalQuality(initialQuality);
    }
    
    // Initialize remote participants
    const initialQualities = new Map();
    room.remoteParticipants.forEach((participant) => {
      initialQualities.set(
        participant.identity, 
        participant.connectionQuality || ConnectionQuality.Unknown
      );
    });
    setRemoteQualities(initialQualities);
    
    // Handler for connection quality changes
    const handleConnectionQualityChanged = (quality, participant) => {
      if (!mountedRef.current) return;
      
      // Log Poor and Lost quality for debugging
      if (quality === ConnectionQuality.Lost) {
        console.warn(`[ConnectionQuality] ${participant.identity}: LOST`);
      } else if (quality === ConnectionQuality.Poor) {
        console.log(`[ConnectionQuality] ${participant.identity}: Poor`);
      }
      
      if (participant === room.localParticipant) {
        setLocalQuality(quality);
        
        // Handle Poor quality with debounce
        if (quality === ConnectionQuality.Poor) {
          // Start timer if not already running
          if (!localPoorTimerRef.current) {
            localPoorTimerRef.current = setTimeout(() => {
              if (mountedRef.current) {
                setIsLocalConnectionUnstable(true);
                console.warn('[ConnectionQuality] Local connection marked as unstable (5s poor)');
              }
              localPoorTimerRef.current = null;
            }, POOR_QUALITY_DEBOUNCE_MS);
          }
        } else {
          // Quality improved or became Lost - clear the poor timer
          clearLocalPoorTimer();
          if (quality !== ConnectionQuality.Lost) {
            setIsLocalConnectionUnstable(false);
          }
        }
        
        // Track consecutive good/excellent readings for stability
        if (quality === ConnectionQuality.Good || quality === ConnectionQuality.Excellent) {
          consecutiveGoodCountRef.current++;
          setIsLocalConnectionUnstable(false);
          
          // Only exit establishing after multiple consecutive good readings
          if (consecutiveGoodCountRef.current >= STABLE_QUALITY_THRESHOLD) {
            setIsEstablishing(false);
            if (establishingTimeoutRef.current) {
              clearTimeout(establishingTimeoutRef.current);
              establishingTimeoutRef.current = null;
            }
          }
        } else {
          // Reset counter on poor/lost/unknown
          consecutiveGoodCountRef.current = 0;
        }
      } else {
        // Remote participant
        const identity = participant.identity;
        
        setRemoteQualities(prev => {
          const next = new Map(prev);
          next.set(identity, quality);
          return next;
        });
        
        // Handle Poor quality with debounce for remote participants
        if (quality === ConnectionQuality.Poor) {
          // Start timer if not already running
          if (!remotePoorTimersRef.current.has(identity)) {
            const timerId = setTimeout(() => {
              if (mountedRef.current) {
                setUnstableParticipants(prev => {
                  const next = new Set(prev);
                  next.add(identity);
                  return next;
                });
                console.warn(`[ConnectionQuality] ${identity} marked as unstable (5s poor)`);
              }
              remotePoorTimersRef.current.delete(identity);
            }, POOR_QUALITY_DEBOUNCE_MS);
            remotePoorTimersRef.current.set(identity, timerId);
          }
        } else {
          // Quality improved or became Lost - clear the poor timer
          clearRemotePoorTimer(identity);
          if (quality !== ConnectionQuality.Lost) {
            setUnstableParticipants(prev => {
              const next = new Set(prev);
              next.delete(identity);
              return next;
            });
          }
        }
      }
    };
    
    // Handler for new participants
    const handleParticipantConnected = (participant) => {
      if (!mountedRef.current) return;
      
      setRemoteQualities(prev => {
        const next = new Map(prev);
        next.set(
          participant.identity, 
          participant.connectionQuality || ConnectionQuality.Unknown
        );
        return next;
      });
    };
    
    // Handler for disconnected participants
    const handleParticipantDisconnected = (participant) => {
      if (!mountedRef.current) return;
      
      const identity = participant.identity;
      
      // Clean up timers and state for this participant
      clearRemotePoorTimer(identity);
      setUnstableParticipants(prev => {
        const next = new Set(prev);
        next.delete(identity);
        return next;
      });
      
      setRemoteQualities(prev => {
        const next = new Map(prev);
        next.delete(identity);
        return next;
      });
    };
    
    room.on(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    
    return () => {
      room.off(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [roomRef, roomConnected, clearLocalPoorTimer, clearRemotePoorTimer]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearLocalPoorTimer();
      remotePoorTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      remotePoorTimersRef.current.clear();
      if (establishingTimeoutRef.current) {
        clearTimeout(establishingTimeoutRef.current);
      }
    };
  }, [clearLocalPoorTimer]);

  return {
    localQuality,
    remoteQualities,
    isLocalConnectionLost,
    isLocalConnectionUnstable,
    isEstablishing,
    getParticipantQuality,
    isParticipantConnectionLost,
    isParticipantConnectionUnstable,
    unstableParticipants,
  };
}

export default useConnectionQuality;
